import { ExternalLink } from 'lucide-react';
import { useEffect } from 'react';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem as SidebarMenuItemWrapper,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { HOME_TOUR_CLASSES } from '@/constants/tour/classNames';
import { SIDEBAR_MENU_ITEMS } from '@/constants/ui/sidebar';
import { useGetLinks } from '@/hooks/useGetLinks';
import { isLockedFunction } from '@/lib/discovery';
import { useStageStore } from '@/stores/stages/useStageStore';
import { useLinkStore } from '@/stores/useLinkStore';

import { DiscoverySubmenu } from '../navigation/DiscoverySubmenu';
import { SidebarMenuItem } from '../navigation/SidebarMenuItem';
import { CustomOrganizationSwitcher } from './OrganizationSwitcher';

export const AppSidebar = () => {
  // state
  const { stageList, featureLocks, discoveryStatus } = useStageStore();
  const { setLinks, setLoading, setError, getVisibleLinks } = useLinkStore();

  // hooks
  const {
    result: linksResult,
    isLoading: isLinksLoading,
    isError: isLinksError,
    isSuccess: isLinksSuccess,
  } = useGetLinks();

  // effects
  useEffect(() => {
    setLoading(isLinksLoading);
  }, [isLinksLoading, setLoading]);

  useEffect(() => {
    if (isLinksError) {
      setError('Failed to load links');
    }
  }, [isLinksError, setError]);

  useEffect(() => {
    if (isLinksSuccess && linksResult?.data) {
      setLinks(linksResult.data);
    }
  }, [isLinksSuccess, linksResult, setLinks]);

  // event handlers
  const checkIsLocked = (feature?: string) => {
    if (feature === 'home') {
      return false;
    }
    if (!feature) {
      return false;
    }

    return isLockedFunction(
      feature as 'territories' | 'financials' | 'library' | 'aimei',
      stageList,
      featureLocks,
      discoveryStatus
    );
  };

  const renderMenuItem = (item: (typeof SIDEBAR_MENU_ITEMS)[0]) => {
    const isLocked = checkIsLocked(item.feature);

    if (item.label === 'Discovery') {
      return (
        <DiscoverySubmenu
          item={item}
          stages={stageList}
          discoveryStatus={
            discoveryStatus || {
              completed_stage_ids: [],
              completed_stage_item_ids: [],
            }
          }
        />
      );
    }

    return (
      <SidebarMenuItem
        icon={item.icon}
        label={item.label}
        path={item.path}
        isLocked={isLocked}
      />
    );
  };

  // Get visible links (filtered for franchisee dashboard)
  const visibleLinks = getVisibleLinks();

  return (
    <Sidebar className="z-20">
      <div className="flex pb-3">
        <CustomOrganizationSwitcher />
      </div>
      <SidebarSeparator className="mb-3" />
      <SidebarContent className="overflow-hidden">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {SIDEBAR_MENU_ITEMS.map((item) => (
                <SidebarMenuItemWrapper key={item.label}>
                  {renderMenuItem(item)}
                </SidebarMenuItemWrapper>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {visibleLinks.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Quick Links</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleLinks.map((link, index) => (
                    <SidebarMenuItemWrapper key={link.id}>
                      <SidebarMenuButton
                        asChild
                        className={
                          index === 0
                            ? HOME_TOUR_CLASSES.SIDEBAR_QUICK_LINK_1_BUTTON
                            : `sidebar-quick-link-${index + 1}-button`
                        }
                      >
                        <a
                          href={
                            link.link?.startsWith('http')
                              ? link.link
                              : `https://${link.link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="size-4" />
                          <span>{link.title}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItemWrapper>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        <SidebarSeparator />
      </SidebarContent>
    </Sidebar>
  );
};
